/**
 * Common API types and interfaces
 * Centralized types to avoid duplication across the application
 */

/**
 * Generic API response wrapper
 */
export interface ApiResponse<T = unknown> {
  data?: T;
  [key: string]: unknown;
}

/**
 * Paginated API response
 */
export interface PaginatedResponse<T> {
  items: T[];
  totalItems?: number;
  lastPage?: number;
  page?: number;
  perPage?: number;
}

/**
 * Common API parameters
 */
export interface ApiParams {
  page?: number;
  limit?: number;
  filter?: {
    where?: Record<string, unknown>;
    include?: Array<{ relation: string }>;
    order?: string[];
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

/**
 * Service Delivery API types
 */
export interface ServiceDeliveryResponse extends ApiResponse {
  calendar?: unknown[];
}

export interface ServiceDeliveryParams extends ApiParams {
  collab_ids?: string[];
  sdt_ids?: string[];
  'pat_ids[]'?: string[];
  week_date?: string;
  empty_pats?: boolean;
}

/**
 * Calendar API types
 */
export interface CalendarParams extends ApiParams {
  collab_ids?: string[];
  sdt_ids?: string[];
  'pat_ids[]'?: string;
  week_date?: string;
  empty_pats?: boolean;
}

/**
 * Patrimony API types
 */
export interface PatrimonyParams extends ApiParams {
  sdt_ids?: string[];
  pat_levels?: string[];
  'filter[where][refPat][like]'?: string;
}

/**
 * User API types
 */
export interface UserCreateData {
  civility: string;
  firstname: string;
  lastname: string;
  agencyId: string;
  roleId: string;
  login: string;
  email: string;
  active: boolean;
}

export interface UserUpdateData extends UserCreateData {
  id: string;
}

/**
 * Job Schedule types
 */
export interface AddJobScheduleProps {
  weekday: string;
  initialTimeRange: string[];
  isIntervention: true;
  status: string;
  outOfStock?: boolean;
  comment: string;
  creatorId: string;
  dayScheduleId: number;
  refPat: string;
  serviceDeliveryTypeIds: string[];
  collabIds: string[];
}

/**
 * Service Delivery creation/update types
 */
export interface ServiceDeliveryCreateData {
  date: string;
  initialTimeRange: string[];
  isIntervention: boolean;
  refPat: string;
  serviceDeliveryTypeIds: string[];
  collabIds: string[];
  comment?: string;
  status?: string;
}

export interface ServiceDeliveryUpdateData {
  date: string;
  initialTimeRange: string[];
  actualTimeRange: string[];
  isIntervention: boolean;
  refPat: string;
  comment: string;
}

export interface ServiceDeliveryStatusUpdateData {
  timeIn: string;
  timeOut: string;
  status: string;
  comment: string;
  outOfStock: boolean;
  date: string;
}

/**
 * Form validation error types
 */
export interface ValidationErrors {
  [key: string]: string | boolean;
}

/**
 * Common form field validation result
 */
export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validation rules for form fields
 */
export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  custom?: (value: unknown) => ValidationResult;
}

/**
 * Form validation schema
 */
export interface ValidationSchema {
  [fieldName: string]: ValidationRule[];
}

/**
 * API Error codes from backend
 */
export const ApiErrorCodes = {
  // User errors
  USER_LOGIN_EXISTS: 'USER_LOGIN_EXISTS',
  USER_INVALID_AGENCY: 'USER_INVALID_AGENCY',
  USER_INVALID_ROLE: 'USER_INVALID_ROLE',
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  USER_INVALID_EMAIL: 'USER_INVALID_EMAIL',
  USER_INVALID_LOGIN_FORMAT: 'USER_INVALID_LOGIN_FORMAT',
  USER_MISSING_REQUIRED_FIELD: 'USER_MISSING_REQUIRED_FIELD',
  USER_INVALID_CIVILITY: 'USER_INVALID_CIVILITY',
  // Agency errors
  AGENCY_NOT_FOUND: 'AGENCY_NOT_FOUND',
  // Service Delivery errors
  SD_INVALID_DATE: 'SD_INVALID_DATE',
  SD_INVALID_PATRIMONY: 'SD_INVALID_PATRIMONY',
  SD_INVALID_TYPE: 'SD_INVALID_TYPE',
  SD_TYPES_REQUIRED: 'SD_TYPES_REQUIRED',
  SD_INVALID_COLLABORATOR: 'SD_INVALID_COLLABORATOR',
  SD_COLLABORATOR_WRONG_AGENCY: 'SD_COLLABORATOR_WRONG_AGENCY',
  SD_INVALID_TIME: 'SD_INVALID_TIME',
  SD_INVALID_WEEK_TEMPLATE: 'SD_INVALID_WEEK_TEMPLATE',
  SD_CREATE_FAILED: 'SD_CREATE_FAILED',
  SD_NOT_FOUND: 'SD_NOT_FOUND',
  // Week Template errors
  WT_INVALID_DAY_SCHEDULE: 'WT_INVALID_DAY_SCHEDULE',
  WT_CORRUPT_DATA: 'WT_CORRUPT_DATA',
  WT_NOT_FOUND: 'WT_NOT_FOUND',
  WT_JOB_CREATE_FAILED: 'WT_JOB_CREATE_FAILED',
  WT_TYPE_ASSOC_FAILED: 'WT_TYPE_ASSOC_FAILED',
  WT_USER_ASSOC_FAILED: 'WT_USER_ASSOC_FAILED',
  // Patrimony errors
  PAT_NOT_FOUND: 'PAT_NOT_FOUND',
  PAT_CREATE_FAILED: 'PAT_CREATE_FAILED',
  PAT_INVALID_TYPES: 'PAT_INVALID_TYPES',
  // Generic errors
  MISSING_PARAMETER: 'MISSING_PARAMETER',
  INVALID_DATA: 'INVALID_DATA',
  SERVER_ERROR: 'SERVER_ERROR',
} as const;

export type ApiErrorCode = typeof ApiErrorCodes[keyof typeof ApiErrorCodes];

/**
 * Structured API error returned by backend
 */
export interface ApiBusinessError {
  code: ApiErrorCode;
  message: string;
  field?: string;
  details?: Record<string, unknown>;
  statusCode?: number;
}

/**
 * Extended Error with API error details
 */
export class ApiError extends Error {
  code?: ApiErrorCode;
  field?: string;
  statusCode?: number;
  details?: Record<string, unknown>;

  constructor(
    message: string,
    code?: ApiErrorCode,
    field?: string,
    statusCode?: number,
    details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.field = field;
    this.statusCode = statusCode;
    this.details = details;
  }
}

